import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  CircularProgress,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowBack, Home } from "@mui/icons-material";
import FileList from "components/fileStorage/FileList";
import FileSearch from "components/fileStorage/FileSearch";
import PaginationControls from "components/fileStorage/PaginationControls";
import { useFilePreview } from "components/preview";
import { useGetIntegrationUserFolderQuery } from "features/integrations/integrations";
import { useGetFileSystemQuery } from "features/fileSystem/filesStorage";
import { useDownloadManager } from "hooks/useDownloadManager";
import { folderMappingsStorage, paginationStorage } from "utils/localStorage";
import { storagePaginationValues } from "utils/constants";
import type { FileItem } from "types/interfaces/filesStorage";
import type { FolderType } from "types/interfaces/filesStorage";

function normPath(s: string): string {
  return (s || "").replace(/\/+$/, "");
}

interface IntegrationFilesPanelProps {
  connectionMatchId: number;
  integrationLabel?: string;
  isSuperuser: boolean;
}

export default function IntegrationFilesPanel({
  connectionMatchId,
  integrationLabel = "Integration folder",
  isSuperuser,
}: IntegrationFilesPanelProps) {
  const [currentPrefix, setCurrentPrefix] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentContinuationToken, setCurrentContinuationToken] = useState<
    string | null
  >(null);
  const [pageTokens, setPageTokens] = useState<string[]>([]);
  const [currentSearch, setCurrentSearch] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState("name_asc");
  const [currentLimit, setCurrentLimit] = useState(() => {
    const saved = paginationStorage.getPaginationValue();
    return storagePaginationValues.includes(saved) ? saved : 25;
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const {
    data: folderMeta,
    isLoading: folderLoading,
    isError: folderError,
    error: folderErrorDetail,
  } = useGetIntegrationUserFolderQuery(connectionMatchId);

  const rootPrefix = normPath(folderMeta?.user_folder?.folder_prefix ?? "");

  useEffect(() => {
    setCurrentPage(1);
    setPageTokens([]);
    setCurrentContinuationToken(null);
    setCurrentSearch(null);
    setSortBy("name_asc");
    setCurrentPrefix(normPath(folderMeta?.user_folder?.folder_prefix ?? ""));
  }, [connectionMatchId, folderMeta?.user_folder?.folder_prefix]);

  const sortField = sortBy.split("_")[0] as "name" | "date" | "size";
  const sortOrder = sortBy.split("_")[1] as "asc" | "desc";
  const apiSortField =
    !isSuperuser && sortField === "date" ? "name" : sortField;
  const apiSortOrder = !isSuperuser && sortField === "date" ? "asc" : sortOrder;

  const skipFileSystem =
    !folderMeta?.success ||
    folderMeta.user_folder == null ||
    !rootPrefix ||
    folderLoading;

  const fileSystemQueryArgs = useMemo(
    () => ({
      prefix: currentPrefix,
      continuation_token: currentContinuationToken,
      search: currentSearch,
      limit: currentLimit,
      folder_type: "all" as FolderType,
      page: currentPage,
      sort_field: apiSortField,
      sort_order: apiSortOrder,
    }),
    [
      currentPrefix,
      currentContinuationToken,
      currentSearch,
      currentLimit,
      currentPage,
      apiSortField,
      apiSortOrder,
    ],
  );

  const {
    data: fileSystemData,
    isLoading: fileSystemLoading,
    isFetching: fileSystemFetching,
    error: fileSystemError,
    refetch: refetchFileSystem,
  } = useGetFileSystemQuery(fileSystemQueryArgs, { skip: skipFileSystem });

  const isDbBacked = !!fileSystemData?.total_pages;
  const downloadManager = useDownloadManager();
  const { openPreview } = useFilePreview();

  useEffect(() => {
    if (fileSystemData?.sync_status !== "syncing") return;
    const t = setInterval(() => void refetchFileSystem(), 3000);
    return () => clearInterval(t);
  }, [fileSystemData?.sync_status, refetchFileSystem]);

  useEffect(() => {
    if (isDbBacked) return;
    if (fileSystemData?.continuation_token && currentPage > 0) {
      setPageTokens((prev) => {
        const next = [...prev];
        if (currentPage > next.length) {
          next.push(fileSystemData.continuation_token!);
        } else {
          next[currentPage - 1] = fileSystemData.continuation_token!;
        }
        return next;
      });
    } else if (!fileSystemData?.continuation_token && currentPage > 0) {
      setPageTokens((prev) => prev.slice(0, currentPage - 1));
    }
  }, [fileSystemData?.continuation_token, currentPage, isDbBacked]);

  useEffect(() => {
    const effectiveSort = sortBy;
    const sortRequiresFiles =
      effectiveSort === "date_desc" ||
      effectiveSort === "date_asc" ||
      effectiveSort === "size_desc" ||
      effectiveSort === "size_asc";
    if (
      isDbBacked &&
      fileSystemData?.has_direct_files === false &&
      sortRequiresFiles
    ) {
      setSortBy("name_asc");
      setCurrentPage(1);
      setPageTokens([]);
      setCurrentContinuationToken(null);
    }
  }, [isDbBacked, fileSystemData?.has_direct_files, sortBy]);

  const isUnderRoot = useCallback(
    (path: string) => {
      const p = normPath(path);
      const r = rootPrefix;
      if (!r) return false;
      return p === r || p.startsWith(`${r}/`);
    },
    [rootPrefix],
  );

  const handleItemClick = (item: FileItem) => {
    if (item.ftype !== "folder") return;
    if (!isUnderRoot(item.fsource)) return;
    const original = fileSystemData?.folders.find(
      (f) => f.fsource === item.fsource,
    );
    const fnameToStore = original?.fname || item.fname;
    folderMappingsStorage.clearIrrelevantMappings(item.fsource);
    folderMappingsStorage.setFolderMapping(fnameToStore, item.fsource);
    setCurrentPrefix(item.fsource);
    setCurrentContinuationToken(null);
    setCurrentPage(1);
    setPageTokens([]);
  };

  const handlePreviewClick = (item: FileItem) => {
    const original =
      fileSystemData?.folders.find((f) => f.fsource === item.fsource) ||
      item;
    openPreview(original);
  };

  const handleBack = () => {
    if (!rootPrefix) return;
    const n = normPath(currentPrefix);
    if (n === rootPrefix) return;
    const rel = n.slice(rootPrefix.length).replace(/^\//, "");
    const parts = rel.split("/").filter(Boolean);
    parts.pop();
    const parent =
      parts.length > 0 ? `${rootPrefix}/${parts.join("/")}` : rootPrefix;
    folderMappingsStorage.clearMappingsForHigherLevel(parent);
    setCurrentPrefix(parent);
    setCurrentContinuationToken(null);
    setCurrentPage(1);
    setPageTokens([]);
  };

  const handleBreadcrumbClick = (targetPrefix: string) => {
    const t = normPath(targetPrefix);
    if (!isUnderRoot(t) && t !== "") return;
    folderMappingsStorage.clearMappingsForHigherLevel(t);
    setCurrentPrefix(t || rootPrefix);
    setCurrentContinuationToken(null);
    setCurrentPage(1);
    setPageTokens([]);
  };

  const buildBreadcrumbs = () => {
    const crumbs: { label: string; path: string; clickable: boolean }[] = [
      {
        label: integrationLabel,
        path: rootPrefix,
        clickable: normPath(currentPrefix) !== rootPrefix,
      },
    ];
    if (!currentPrefix || normPath(currentPrefix) === rootPrefix) {
      return crumbs;
    }
    const rel = normPath(currentPrefix)
      .slice(rootPrefix.length)
      .replace(/^\//, "");
    const parts = rel.split("/").filter(Boolean);
    let acc = rootPrefix;
    for (let i = 0; i < parts.length; i++) {
      acc = `${acc}/${parts[i]}`;
      const isLast = i === parts.length - 1;
      const mapped = folderMappingsStorage.getFname(acc);
      crumbs.push({
        label: mapped || parts[i],
        path: acc,
        clickable: !isLast,
      });
    }
    return crumbs;
  };

  const handleLimitChange = (limit: number) => {
    paginationStorage.setValidPaginationValue(limit, storagePaginationValues);
    setCurrentLimit(limit);
    setCurrentPage(1);
    setCurrentContinuationToken(null);
    setPageTokens([]);
  };

  const handleSortChange = (next: string) => {
    setSortBy(next);
    setCurrentPage(1);
    setCurrentContinuationToken(null);
    setPageTokens([]);
  };

  const handlePageChange = (page: number) => {
    if (isDbBacked) {
      if (page < 1 || page > (fileSystemData?.total_pages || 1)) return;
      setCurrentPage(page);
      return;
    }
    const maxPages = fileSystemData?.continuation_token
      ? pageTokens.length + 1
      : currentPage;
    if (page < 1 || page > maxPages) return;
    if (page === 1) {
      setCurrentContinuationToken(null);
    } else if (page <= pageTokens.length + 1) {
      setCurrentContinuationToken(pageTokens[page - 2]);
    } else {
      return;
    }
    setCurrentPage(page);
  };

  const handleSearch = (term: string | null) => {
    setCurrentSearch(term);
    setCurrentPage(1);
    setCurrentContinuationToken(null);
    setPageTokens([]);
  };

  const handleDownloadClick = async (item: FileItem) => {
    const original =
      fileSystemData?.folders.find((f) => f.fsource === item.fsource) || item;
    const result = await downloadManager.downloadItem(original);
    setSnackbar({
      open: true,
      message: result.message,
      severity: result.success ? "success" : "error",
    });
  };

  const sortSelectValue = sortBy;

  const displayedFolders = useMemo(() => {
    const rows = fileSystemData?.folders ?? [];
    if (apiSortField !== "name") return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const cmp = a.fname.localeCompare(b.fname, undefined, {
        sensitivity: "base",
        numeric: true,
      });
      return apiSortOrder === "desc" ? -cmp : cmp;
    });
    return copy;
  }, [fileSystemData?.folders, apiSortField, apiSortOrder]);

  if (folderLoading) {
    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          border: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (folderError) {
    const msg =
      (folderErrorDetail as { data?: { error?: string } })?.data?.error ||
      "Failed to load folder link";
    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          border: "1px solid",
          borderColor: "divider",
          p: 2,
        }}
      >
        <Alert severity="error">{msg}</Alert>
      </Box>
    );
  }

  if (!folderMeta?.user_folder) {
    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          border: "1px solid",
          borderColor: "divider",
          p: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {folderMeta?.message ||
            "No Data Storage folder is linked to this integration yet. Connect a folder from Data Storage (super admin) to browse files here."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          mb: 1.5,
          flexShrink: 0,
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1, sm: 1 },
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          border: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
        }}
      >
        {normPath(currentPrefix) !== rootPrefix && (
          <IconButton
            onClick={handleBack}
            aria-label="Back"
            size="small"
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              backgroundColor: "background.paper",
              color: "primary.main",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        )}
        <Breadcrumbs
          aria-label="breadcrumb"
          separator="/"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            "& .MuiBreadcrumbs-ol": {
              flexWrap: "nowrap",
              overflow: "hidden",
            },
            "& .MuiBreadcrumbs-li": {
              maxWidth: "100%",
              overflow: "hidden",
              display: "flex",
            },
          }}
        >
          {buildBreadcrumbs().map((c, i) =>
            c.clickable ? (
              <Tooltip key={`${c.path}-${i}`} title={c.label}>
                <Link
                  component="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => handleBreadcrumbClick(c.path)}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    border: "none",
                    background: "none",
                    p: 0,
                    font: "inherit",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: "left",
                  }}
                >
                  {i === 0 && <Home fontSize="small" sx={{ flexShrink: 0 }} />}
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    {c.label}
                  </Box>
                </Link>
              </Tooltip>
            ) : (
              <Tooltip key={`${c.path}-${i}`} title={c.label}>
                <Typography
                  variant="body2"
                  color="text.primary"
                  fontWeight={600}
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  {i === 0 && <Home fontSize="small" sx={{ flexShrink: 0 }} />}
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    {c.label}
                  </Box>
                </Typography>
              </Tooltip>
            ),
          )}
        </Breadcrumbs>

        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            gap: 1,
            height: 40,
            flexBasis: { xs: "100%", sm: "auto" },
            width: { xs: "100%", sm: "auto" },
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: { xs: "none", sm: 400 },
            }}
          >
            <FileSearch
              key={connectionMatchId}
              onSearch={handleSearch}
              placeholder="Search files and folders..."
              initialValue={currentSearch ?? ""}
            />
          </Box>
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: 140, sm: 170 },
              height: "100%",
              flexShrink: 0,
              "& .MuiInputBase-root": { height: "100%" },
            }}
          >
            <Select<string>
              value={sortSelectValue}
              onChange={(e: SelectChangeEvent<string>) =>
                handleSortChange(String(e.target.value))
              }
              sx={{ fontSize: "0.85rem", height: "100%" }}
            >
              {isSuperuser
                ? [
                    <MenuItem
                      key="date_desc"
                      value="date_desc"
                      disabled={!fileSystemData?.has_direct_files}
                    >
                      Date (Newest first)
                    </MenuItem>,
                    <MenuItem
                      key="date_asc"
                      value="date_asc"
                      disabled={!fileSystemData?.has_direct_files}
                    >
                      Date (Oldest first)
                    </MenuItem>,
                  ]
                : null}
              <MenuItem
                value="size_desc"
                disabled={!fileSystemData?.has_direct_files}
              >
                Size (Largest first)
              </MenuItem>
              <MenuItem
                value="size_asc"
                disabled={!fileSystemData?.has_direct_files}
              >
                Size (Smallest first)
              </MenuItem>
              <MenuItem value="name_asc">Name (A → Z)</MenuItem>
              <MenuItem value="name_desc">Name (Z → A)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box
        sx={{
          flex: "1 1 0",
          minHeight: 0,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <FileList
            showFileDate={isSuperuser}
            timeDisplayMode="local"
            hideEmptyLabel={normPath(currentPrefix) === rootPrefix}
            items={displayedFolders}
            loading={fileSystemLoading || fileSystemFetching}
            error={fileSystemError ? "Failed to load files" : null}
            currentLimit={currentLimit}
            onLimitChange={handleLimitChange}
            onItemClick={handleItemClick}
            onDownloadClick={handleDownloadClick}
            onPreviewClick={handlePreviewClick}
            showDownloadButton
            showPreviewButton
            disableFileClick
            showMoreOptions={false}
            showPagination
            size="medium"
            variant="default"
            maxHeight="100%"
            currentPage={currentPage}
            totalCount={fileSystemData?.total_count}
            totalPages={
              isDbBacked
                ? (fileSystemData?.total_pages ?? 1)
                : fileSystemData?.continuation_token
                  ? pageTokens.length + 1
                  : currentPage
            }
            onPageChange={handlePageChange}
            pageTokens={pageTokens}
            downloadingItems={downloadManager.downloadingItems}
            downloadProgress={downloadManager.downloadProgress}
            hasNextPage={
              isDbBacked
                ? currentPage < (fileSystemData?.total_pages || 1)
                : !!fileSystemData?.continuation_token
            }
          />
        </Box>
      </Box>

      {fileSystemData?.sync_status === "syncing" && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, flexShrink: 0 }}
        >
          Folder sync in progress…
        </Typography>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
